import { Form, Input, InputNumber, Pagination, Select, Switch } from 'antd';
import { useEffect, useState } from 'react';

import { apiFetch } from '@/api/client';
import { goodsReceiptsKey } from '@/api/goodsReceipts';
import {
  itemUnitsKey,
  itemVariantsKey,
  useItemUnits,
  useItemVariants,
  useItems,
  type ItemUnit,
  type ItemVariant,
  type ItemVariantAxis,
} from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import {
  UnitConditionValues,
  UnitStatusValues,
  type UnitCondition,
  type UnitStatus,
} from '@/api/generated/enums';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { DatePicker } from '@/components/DatePicker';
import { FormDrawer } from '@/components/FormDrawer';
import { GarmentImage } from '@/components/GarmentImage';
import { IconAction } from '@/components/IconAction';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge, type StatusTone } from '@/components/StatusBadge';
import { useDebounced } from '@/components/useDebounced';
import { RecordAction, WriteAction } from '@/components/WriteAction';
import { absent, formatDate, toIsoDate, toDayjs } from '@core/format/dates';
import { humanizeEnum } from '@core/format/text';
import type { Session } from '@core/session/types';
import { referenceLabel } from '@core/units/referenceNumber';

import { MediaDrawer } from './MediaDrawer';
import { GarmentTimeline } from './units/GarmentTimeline';
import { StockImportPage } from './units/import/StockImportPage';
import { ReceiveStockPage } from './units/ReceiveStockPage';
import { StockIntakePage } from './units/StockIntakePage';
import { toBatchRequest, type IntakeDraft } from './units/intake';

const pageSize = 25;

/** What `POST /api/item-unit/batch` answers with. The count, and the delivery's goods receipt. */
interface BatchResult {
  totalUnits: number;
  goodsReceiptNumber: string | null;
}

/**
 * The rail — one row per physical garment (spec Section 6).
 *
 * <p>
 * This is the screen the four-layer catalogue was built to make possible, and the thing to
 * understand about it is what it deliberately does <em>not</em> ask for. A storeman books in five
 * navy 40s; they do not first declare that "navy 40" is a configuration the shop stocks. The
 * server finds or creates the variant from the axis values picked in the drawer, generates five
 * serials from its code, and writes five rows. There is no variant picker and no "new variant"
 * button anywhere here.
 * </p>
 *
 * <p>
 * The variants that result are reachable, though — filter to one design and the Variants button
 * opens them, which is where a rate override or a colour photo goes. They are shown per design
 * rather than as a list of their own because a variant means nothing apart from the item it
 * configures. The tabbed item-detail screen the design doc sketches (§8) wants route parameters
 * the shell's key-based navigation does not have yet; this is the same content under the
 * navigation that exists.
 * </p>
 */
export function UnitsPage({
  session,
  initialSearch = '',
}: {
  session: Session;
  /** A serial the global find resolved, already in the search box when the screen opens. */
  initialSearch?: string | undefined;
}) {
  const [search, setSearch] = useState(initialSearch);
  const [itemId, setItemId] = useState<number | undefined>();
  const [status, setStatus] = useState<UnitStatus | undefined>();
  const [page, setPage] = useState(1);

  // Read once, not per render: a delivery keyed in across midnight must not have its purchase date
  // move under the storeman half way through.
  const [today] = useState(() => toIsoDate(new Date()) ?? '');

  // Book in stock opens the phone's flow ('receive'); the per-line form ('lines') stays for
  // garments that already carry tags (owner, 2026-10-23: "like mobile flow in web").
  const [adding, setAdding] = useState<false | 'receive' | 'lines' | 'import'>(false);
  const [editing, setEditing] = useState<ItemUnit | null>(null);
  const [moving, setMoving] = useState<ItemUnit | null>(null);
  const [history, setHistory] = useState<ItemUnit | null>(null);
  const [picturing, setPicturing] = useState<ItemUnit | null>(null);
  const [variantsFor, setVariantsFor] = useState<number | null>(null);

  const debouncedSearch = useDebounced(search);

  // Any narrowing invalidates the page number: filtering to a design while on page 4 of the whole
  // rail lands on an empty page, which reads as "no garments" rather than "wrong page".
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, itemId, status]);

  const units = useItemUnits(session.branch?.id, {
    search: debouncedSearch,
    itemId,
    status,
    page,
    pageSize,
  });

  // A page of designs large enough to cover the filter dropdown without paging it. Inactive
  // included: a retired design still has garments coming back, and a filter that cannot name it
  // makes those unreachable.
  const items = useItems(session.branch?.id, { includeInactive: true, pageSize: 200 });

  const remove = useApiMutation<void, ItemUnit>({
    mutationFn: (unit) => apiFetch<void>(`/api/item-unit/${unit.id}`, { method: 'DELETE' }),
    success: (_result, unit) => `${unit.serialCode} deleted.`,
    invalidates: [itemUnitsKey, itemVariantsKey],
  });

  // One call for the whole delivery, so a refusal on any line writes none of them. The count comes
  // back from the server rather than being totalled here — the same number, decided once.
  const bookIn = useApiMutation<BatchResult, IntakeDraft>({
    mutationFn: (draft) =>
      apiFetch<BatchResult>('/api/item-unit/batch', {
        method: 'POST',
        body: toBatchRequest(draft),
      }),
    success: (result) =>
      `${result.totalUnits} garment${result.totalUnits === 1 ? '' : 's'} booked in` +
      (result.goodsReceiptNumber ? ` on ${result.goodsReceiptNumber}.` : '.'),
    invalidates: [itemUnitsKey, itemVariantsKey, goodsReceiptsKey],
    onSuccess: () => setAdding(false),
  });

  const columns: DataViewColumn<ItemUnit>[] = [
    {
      title: 'Garment',
      dataIndex: 'serialCode',
      primary: true,
      render: (_, row) => (
        <div className="flex items-center gap-2.5">
          {/* Opens this garment's own gallery. A rail is a wall of near-identical serial codes, and
              the picture is what tells two navy tuxedos apart at a glance. */}
          <GarmentImage
            url={row.imageUrl}
            alt={row.serialCode}
            size="sm"
            onClick={() => setPicturing(row)}
          />

          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="font-mono-nums font-semibold text-gray-900">
                {row.serialCode}
                {row.referenceNumber && (
                  <span className="text-default ms-1">({referenceLabel(row.referenceNumber)})</span>
                )}
              </span>
              <StatusBadge tone={statusTone(row.status)}>{humanizeEnum(row.status)}</StatusBadge>
            </div>
            <span className="text-default truncate text-[12px]">
              {row.itemName}
              {row.variantDisplayName ? ` · ${row.variantDisplayName}` : ''}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: 'Configuration',
      key: 'axes',
      width: 200,
      responsive: ['lg'],
      // Off the card: the heading above already carries it, and repeating it as a labelled row is
      // the same string twice on the narrowest screen.
      hideOnCard: true,
      render: (_, row) => <Axes axes={row.axes} />,
    },
    {
      title: 'Condition',
      dataIndex: 'condition',
      width: 130,
      render: (_, row) => (
        <StatusBadge tone={conditionTone(row.condition)}>{humanizeEnum(row.condition)}</StatusBadge>
      ),
    },
    {
      // The standard hire price for this garment, resolved through the same chain a booking uses —
      // its own card, else its configuration's, else its design's, else the category's — with its
      // own modifier applied. A dash means nothing prices it, which is setup left unfinished rather
      // than a garment that goes out free.
      title: 'Rate',
      dataIndex: 'effectiveRate',
      width: 130,
      align: 'right',
      render: (_, row) => (
        <div className="flex flex-col items-end">
          <Amount value={row.effectiveRate} />
          {row.rateAdjustmentPct !== null && row.rateAdjustmentPct !== 0 && (
            <span className="text-[11px] text-default">
              {row.rateAdjustmentPct > 0 ? '+' : ''}
              {row.rateAdjustmentPct}% this garment
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Rentals',
      dataIndex: 'rentalCount',
      width: 110,
      align: 'right',
      responsive: ['xl'],
      render: (_, row) => (
        <div className="flex flex-col items-end">
          <span className="font-mono-nums">{row.rentalCount}</span>
          {row.lastRentedOn && (
            <span className="text-[11px] text-default">{formatDate(row.lastRentedOn)}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Cost',
      dataIndex: 'purchaseCost',
      width: 120,
      align: 'right',
      responsive: ['xl'],
      hideOnCard: true,
      render: (_, row) => <Amount value={row.purchaseCost} />,
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      align: 'right',
      fixed: 'right',
      trailing: true,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <IconAction
            icon="icon icon-camera"
            label={`Photographs of ${row.serialCode}`}
            onClick={() => setPicturing(row)}
          />

          <IconAction
            icon="icon icon-history"
            label={`History of ${row.serialCode}`}
            onClick={() => setHistory(row)}
          />

          <WriteAction session={session} required="ItemUnit:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-arrow-right-left"
                label={`Move ${row.serialCode} to another state`}
                disabled={disabled}
                onClick={() => setMoving(row)}
              />
            )}
          </WriteAction>

          <WriteAction session={session} required="ItemUnit:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-pencil-line"
                label={`Edit ${row.serialCode}`}
                disabled={disabled}
                onClick={() => setEditing(row)}
              />
            )}
          </WriteAction>

          <RecordAction session={session} required="ItemUnit:Delete">
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-trash-2"
                label={`Delete ${row.serialCode}`}
                title="Delete this garment?"
                // Retiring is what keeps the rental count and the earnings, which is the whole of
                // Section 20's input. Deleting is for the serial someone typed twice.
                description="Only for something booked in by mistake. If the garment is worn out or sold on, move it to Retired instead — that keeps its history and earnings."
                confirmText="Delete"
                disabled={disabled}
                pending={remove.isPending}
                onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
              />
            )}
          </RecordAction>
        </div>
      ),
    },
  ];

  // A full page rather than a drawer, and it replaces the list rather than sitting over it: a
  // delivery is eleven lines against dates and costs, which a 560px drawer could show only a
  // third of at a time. Same move `NewBookingPage` made at the counter, for the same reason.
  if (adding === 'receive') {
    return (
      <ReceiveStockPage
        session={session}
        today={today}
        invalidates={[itemUnitsKey, itemVariantsKey, goodsReceiptsKey]}
        onClose={() => setAdding(false)}
        onDetailed={() => setAdding('lines')}
      />
    );
  }

  if (adding === 'import') {
    return (
      <StockImportPage
        session={session}
        today={today}
        invalidates={[itemUnitsKey, itemVariantsKey, goodsReceiptsKey]}
        onClose={() => setAdding(false)}
      />
    );
  }

  if (adding === 'lines') {
    return (
      <StockIntakePage
        today={today}
        items={items.data?.items ?? []}
        branchId={session.branch?.id}
        branchName={session.branch?.name ?? 'this branch'}
        currency="₹"
        busy={bookIn.isPending}
        onClose={() => setAdding(false)}
        onSubmit={(draft) => void bookIn.mutateAsync(draft).catch(() => undefined)}
      />
    );
  }

  if (units.isError) {
    return <ApiErrorAlert error={units.error} />;
  }

  const total = units.data?.total ?? 0;
  const itemOptions = (items.data?.items ?? []).map((item) => ({
    label: `${item.name} (${item.code})`,
    value: item.id,
  }));

  return (
    <>
      <PageHeading
        title="Item units"
        subtitle="Every garment on the rail, one row each. Serials, condition and where each one is."
        actions={
          <div className="flex gap-2">
            {itemId !== undefined && (
              <Button
                variant="secondary"
                icon="icon icon-layers"
                onClick={() => setVariantsFor(itemId)}
              >
                Variants
              </Button>
            )}

            <WriteAction session={session} required="ItemUnit:Create">
              {(disabled) => (
                <Button
                  icon="icon icon-file-spreadsheet"
                  disabled={disabled}
                  onClick={() => setAdding('import')}
                >
                  Import from Excel
                </Button>
              )}
            </WriteAction>

            <WriteAction session={session} required="ItemUnit:Create">
              {(disabled) => (
                <Button
                  variant="primary"
                  icon="icon icon-plus"
                  shortcut="create"
                  disabled={disabled}
                  onClick={() => setAdding('receive')}
                >
                  Book in stock
                </Button>
              )}
            </WriteAction>
          </div>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <div className="mb-4 flex flex-wrap items-center gap-3 max-sm:mb-3 max-sm:gap-2">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search serial, code or name"
            allowClear
            prefix={<i className="icon icon-search text-default" />}
            className="max-w-72 max-sm:max-w-none max-sm:flex-[1_0_100%]"
          />

          <Select
            value={itemId}
            onChange={setItemId}
            options={itemOptions}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Every item"
            className="min-w-56 max-sm:min-w-0 max-sm:flex-1"
          />

          <Select
            value={status}
            onChange={setStatus}
            options={UnitStatusValues.map((value) => ({
              label: humanizeEnum(value),
              value,
            }))}
            allowClear
            placeholder="Any state"
            className="min-w-44 max-sm:min-w-0 max-sm:flex-1"
          />
        </div>

        <DataView<ItemUnit>
          columns={columns}
          dataSource={units.data?.items ?? []}
          loading={units.isLoading}
          empty={
            <EmptyState
              icon="icon icon-tag"
              title={
                debouncedSearch || itemId !== undefined || status
                  ? 'Nothing matches those filters'
                  : 'No garments at this branch'
              }
              hint={
                debouncedSearch || itemId !== undefined || status
                  ? 'Clear the search, or pick a different item or state.'
                  : `Book in stock to start. Garments belong to ${
                      session.branch?.code ?? 'this branch'
                    } and are not shared the way the catalogue above them is.`
              }
            />
          }
        />

        {total > pageSize && (
          <div className="flex justify-end border-t border-border-color py-3">
            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              onChange={setPage}
              showSizeChanger={false}
              showTotal={(count) => `${count} garments`}
            />
          </div>
        )}
      </Card>

      {editing && <EditUnitDrawer unit={editing} onClose={() => setEditing(null)} />}

      {moving && <MoveStatusDrawer unit={moving} onClose={() => setMoving(null)} />}

      {history && (
        <GarmentTimeline
          unitId={history.id}
          serialCode={history.serialCode}
          subtitle={`${history.itemName} · ${history.variantDisplayName}`}
          onClose={() => setHistory(null)}
        />
      )}

      {picturing && (
        <MediaDrawer
          session={session}
          scope={{ level: 'unit', unitId: picturing.id, name: picturing.serialCode }}
          onClose={() => setPicturing(null)}
        />
      )}

      {variantsFor !== null && (
        <VariantsDrawer
          itemId={variantsFor}
          session={session}
          onClose={() => setVariantsFor(null)}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */

interface EditUnitValues {
  condition: UnitCondition;
  purchaseCost?: number | null;
  purchasedOn?: unknown;
  rateAdjustmentPct?: number | null;
  rentPerDay?: number | null;
  notes?: string | null;
  referenceNumber?: string | null;
}

/**
 * What may be corrected on a garment already on the rail.
 *
 * Serial and status are both absent, for different reasons. The serial is stitched into the
 * garment, so changing it here only makes the software disagree with the rail. The status moves
 * through its own drawer, so nothing changes state without a history row behind it.
 */
function EditUnitDrawer({ unit, onClose }: { unit: ItemUnit; onClose: () => void }) {
  const [form] = Form.useForm<EditUnitValues>();

  const save = useApiMutation<unknown, EditUnitValues>({
    mutationFn: (values) =>
      apiFetch(`/api/item-unit/${unit.id}`, {
        method: 'PUT',
        body: {
          condition: values.condition,
          purchaseCost: values.purchaseCost ?? null,
          purchasedOn: toIsoDate(values.purchasedOn) ?? null,
          rateAdjustmentPct: values.rateAdjustmentPct ?? null,
          rentPerDay: values.rentPerDay ?? null,
          notes: values.notes || null,
          // Blank takes the shop's number away; the server cleans "007" to "7".
          referenceNumber: { value: values.referenceNumber?.trim() || null },
        },
      }),
    success: `${unit.serialCode} updated.`,
    invalidates: [itemUnitsKey],
    onSuccess: onClose,
  });

  return (
    <FormDrawer<EditUnitValues>
      title={`Edit ${unit.serialCode}`}
      subtitle={`${unit.itemName} · ${unit.variantDisplayName}`}
      open
      onClose={onClose}
      form={form}
      initialValues={{
        condition: unit.condition,
        purchaseCost: unit.purchaseCost,
        purchasedOn: toDayjs(unit.purchasedOn),
        rateAdjustmentPct: unit.rateAdjustmentPct,
        rentPerDay: unit.rentPerDay,
        notes: unit.notes,
        referenceNumber: unit.referenceNumber ?? null,
      }}
      onSubmit={(values) => void submitVia(save, values)}
      submitText="Save"
      submitting={save.isPending}
    >
      <Form.Item name="condition" label="Condition">
        <Select
          options={UnitConditionValues.map((value) => ({ label: humanizeEnum(value), value }))}
        />
      </Form.Item>

      {/*
        The shop's own number (RN), shown beside the serial — "BLZ-38-03 (RN-7)". Unlike the serial
        it can be corrected here, since it is the shop's to keep; another garment's number is refused
        by name.
      */}
      <Form.Item
        name="referenceNumber"
        label="Reference number (RN)"
        extra="The shop's own number on this garment's tag, if it has one. Blank for none."
      >
        <Input maxLength={20} placeholder="—" />
      </Form.Item>

      <Form.Item name="purchaseCost" label="Purchase cost">
        <InputNumber min={0} precision={2} className="w-full" />
      </Form.Item>

      <Form.Item name="purchasedOn" label="Purchased on">
        <DatePicker className="w-full" />
      </Form.Item>

      {/*
        A modifier, not a price — the rate card is a ladder and this is one number, so it rides on
        top of whichever card prices the design. This is how the newer of two identical sherwanis
        rents for more without anybody writing a second card. A garment that needs its own ladder
        gets one on Rate cards, and then this is not applied on top of it.
      */}
      <Form.Item
        name="rateAdjustmentPct"
        label="Rate adjustment"
        extra="What this particular garment is worth against its design's card. Blank means no change; negatives are legitimate for a tired garment."
      >
        <InputNumber precision={2} addonAfter="%" className="w-full" placeholder="0" />
      </Form.Item>

      {/*
        A price, not a modifier — the other half of the pair above, and a shop uses one or the other.
        Clearing it here *does* withdraw it, unlike leaving it blank at intake: an edit form states
        the garment's whole intended state, so an empty box is a decision to let this garment fall
        back to whatever its configuration is priced at.

        Blank is also what a garment carrying a hand-built ladder shows, because one box cannot
        represent three bands — which is why the hint sends that case to Rate cards rather than
        letting a save here flatten it.
      */}
      <Form.Item
        name="rentPerDay"
        label="Price per day"
        extra="This garment's own flat rate, charged exactly as typed with no adjustment on top. Blank means it is priced by its configuration or design. A garment needing a duration ladder gets one on Rate cards."
      >
        <InputNumber min={0} precision={2} className="w-full" placeholder="Inherited" />
      </Form.Item>

      <Form.Item name="notes" label="Notes">
        <Input.TextArea rows={3} />
      </Form.Item>
    </FormDrawer>
  );
}

/* -------------------------------------------------------------------------- */

interface MoveStatusValues {
  status: UnitStatus;
  reason?: string | null;
}

/** Moving a garment between states, which is the only way `status` changes. */
function MoveStatusDrawer({ unit, onClose }: { unit: ItemUnit; onClose: () => void }) {
  const [form] = Form.useForm<MoveStatusValues>();

  const save = useApiMutation<unknown, MoveStatusValues>({
    mutationFn: (values) =>
      apiFetch(`/api/item-unit/${unit.id}/status`, {
        method: 'POST',
        body: { status: values.status, reason: values.reason || null },
      }),
    success: (_result, values) => `${unit.serialCode} moved to ${humanizeEnum(values.status)}.`,
    invalidates: [itemUnitsKey],
    onSuccess: onClose,
  });

  const terminal = unit.status === 'Lost' || unit.status === 'Retired';

  return (
    <FormDrawer<MoveStatusValues>
      title={`Move ${unit.serialCode}`}
      subtitle={`Currently ${humanizeEnum(unit.status)}`}
      open
      onClose={onClose}
      form={form}
      initialValues={{ status: unit.status }}
      onSubmit={(values) => void submitVia(save, values)}
      submitText="Move"
      submitting={save.isPending}
    >
      {terminal ? (
        <Notice tone="warning" title="This garment is recorded as gone">
          Lost and Retired are the end of a garment&apos;s life. Its rental count and earnings stay
          in the reports, which is why it cannot be brought back to the rail.
        </Notice>
      ) : (
        <>
          <Form.Item
            name="status"
            label="Move to"
            rules={[{ required: true, message: 'Pick a state.' }]}
          >
            <Select
              options={UnitStatusValues.filter((value) => value !== unit.status).map((value) => ({
                label: humanizeEnum(value),
                value,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Why"
            extra="Worth writing. A list of state changes with no reasons is not a maintenance record."
          >
            <Input.TextArea rows={3} placeholder="Torn at the Patel wedding" />
          </Form.Item>

          <Notice tone="info" title="This is not the booking calendar">
            State says where a garment is today. Whether it is free on a given date is Section
            7&apos;s availability, which is not built yet.
          </Notice>
        </>
      )}
    </FormDrawer>
  );
}

/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */

/**
 * One design's configurations, and the two things a merchandiser sets on them.
 *
 * No create control, deliberately — see `UnitsPage`. What a screen can do to a variant is override
 * its rate, give it its own photo, and retire it. Everything else about it is the combination it
 * stands for, and that is fixed by the garments already tagged with it.
 */
function VariantsDrawer({
  itemId,
  session,
  onClose,
}: {
  itemId: number;
  session: Session;
  onClose: () => void;
}) {
  const [form] = Form.useForm();
  const variants = useItemVariants(itemId);
  const [editing, setEditing] = useState<ItemVariant | null>(null);

  const columns: DataViewColumn<ItemVariant>[] = [
    {
      title: 'Configuration',
      dataIndex: 'displayName',
      primary: true,
      render: (_, row) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{row.displayName}</span>
            {!row.isActive && <StatusBadge tone="warning">Retired</StatusBadge>}
          </div>
          <span className="font-mono-nums text-[12px] text-default">{row.code}</span>
        </div>
      ),
    },
    {
      // The variant list no longer carries a rate: a configuration's price is a card, and a card is
      // a ladder. What it does carry is how many garments hang off it, which is what this list is
      // read for. The price is on the Rate cards screen, whole.
      title: 'Axis modifier',
      key: 'adjustment',
      width: 130,
      align: 'right',
      render: (_, row) => {
        const adjustment = row.axes.reduce(
          (total, axis) => total + (axis.rateAdjustmentPct ?? 0),
          0,
        );

        return adjustment === 0 ? (
          <span className="text-default">{absent}</span>
        ) : (
          <span className="font-mono-nums">
            {adjustment > 0 ? '+' : ''}
            {adjustment}%
          </span>
        );
      },
    },
    {
      title: 'Garments',
      dataIndex: 'unitCount',
      width: 110,
      align: 'right',
      render: (_, row) => <span className="font-mono-nums">{row.unitCount}</span>,
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      align: 'right',
      trailing: true,
      render: (_, row) => (
        <WriteAction session={session} required="Item:Edit">
          {(disabled) => (
            <IconAction
              icon="icon icon-pencil-line"
              label={`Edit ${row.displayName}`}
              disabled={disabled}
              onClick={() => setEditing(row)}
            />
          )}
        </WriteAction>
      ),
    },
  ];

  return (
    <>
      <FormDrawer
        title="Variants"
        subtitle="Created when stock is booked in against a new combination — there is nothing to add by hand."
        open
        onClose={onClose}
        form={form}
        onSubmit={onClose}
        submitText="Close"
        width={640}
      >
        {variants.isError && <ApiErrorAlert error={variants.error} />}

        <DataView<ItemVariant>
          columns={columns}
          dataSource={variants.data ?? []}
          loading={variants.isLoading}
          empty={
            <EmptyState
              icon="icon icon-layers"
              title="No configurations yet"
              hint="Book in a garment and the configuration it arrives as will appear here."
            />
          }
        />
      </FormDrawer>

      {editing && (
        <EditVariantDrawer itemId={itemId} variant={editing} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

interface EditVariantValues {
  primaryImageUrl?: string | null;
  isActive: boolean;
}

function EditVariantDrawer({
  itemId,
  variant,
  onClose,
}: {
  itemId: number;
  variant: ItemVariant;
  onClose: () => void;
}) {
  const [form] = Form.useForm<EditVariantValues>();

  const save = useApiMutation<unknown, EditVariantValues>({
    mutationFn: (values) =>
      apiFetch(`/api/item/${itemId}/variants/${variant.id}`, {
        method: 'PUT',
        body: {
          primaryImageUrl: values.primaryImageUrl || null,
          isActive: values.isActive,
        },
      }),
    success: `${variant.displayName} updated.`,
    invalidates: [itemVariantsKey, itemUnitsKey],
    onSuccess: onClose,
  });

  const adjustment = variant.axes.reduce((total, axis) => total + (axis.rateAdjustmentPct ?? 0), 0);

  return (
    <FormDrawer<EditVariantValues>
      title={variant.displayName}
      subtitle={variant.code}
      open
      onClose={onClose}
      form={form}
      initialValues={{
        primaryImageUrl: variant.primaryImageUrl,
        isActive: variant.isActive,
      }}
      onSubmit={(values) => void submitVia(save, values)}
      submitText="Save"
      submitting={save.isPending}
    >
      {/*
        No rate field here any more. A configuration is priced by a rate card owned by it — a ladder
        rather than one number — and that is maintained on the Rate cards screen. What this drawer
        can still say usefully is what the chosen values do to the design's price on their own.
      */}
      <Notice title="Pricing lives on the rate card">
        {adjustment === 0
          ? 'This configuration inherits its design’s card. Give it its own on Rate cards if it should cost differently.'
          : `These values carry ${adjustment > 0 ? '+' : ''}${adjustment}%, applied to whichever card prices the design. A card written against this configuration replaces that rather than adding to it.`}
      </Notice>

      <Form.Item name="primaryImageUrl" label="Photo URL" className="mt-4">
        <Input placeholder="https://…" />
      </Form.Item>

      <Form.Item
        name="isActive"
        label="Offered"
        valuePropName="checked"
        extra={
          variant.unitCount > 0
            ? `${variant.unitCount} garment${variant.unitCount === 1 ? '' : 's'} carry this configuration. Retiring it stops it being offered without orphaning them.`
            : 'Retiring stops this combination being offered on the counter screen.'
        }
      >
        <Switch />
      </Form.Item>
    </FormDrawer>
  );
}

/* -------------------------------------------------------------------------- */

/** The axes of a configuration, with the swatch where there is one. */
function Axes({ axes }: { axes: ItemVariantAxis[] }) {
  if (axes.length === 0) {
    return <span className="text-default">{absent}</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {axes.map((axis) => (
        <span
          key={axis.attributeId}
          className="inline-flex items-center gap-1 text-[12px] text-default"
        >
          {axis.colourHex && (
            <span
              className="inline-block size-3 rounded-full border border-border-color"
              style={{ backgroundColor: axis.colourHex }}
              // The swatch repeats what the name already says, so it is decoration to a reader
              // and to a screen reader alike.
              aria-hidden="true"
            />
          )}
          {axis.valueName}
        </span>
      ))}
    </div>
  );
}

function Amount({ value }: { value: number | null }) {
  if (value === null) {
    return <span className="text-default">{absent}</span>;
  }

  return <span className="font-mono-nums">{value.toLocaleString('en-IN')}</span>;
}

/**
 * A garment's state, as a tone.
 *
 * Available is the only positive: everything else is a garment that cannot be given out today, and
 * painting "In cleaning" green would make a stock list read as fuller than the rail is.
 */
function statusTone(status: UnitStatus): StatusTone {
  switch (status) {
    case 'Available':
      return 'positive';
    case 'OnRent':
    case 'Reserved':
      return 'brand';
    case 'Damaged':
    case 'Lost':
      return 'negative';
    case 'Retired':
      return 'neutral';
    default:
      return 'warning';
  }
}

function conditionTone(condition: UnitCondition): StatusTone {
  switch (condition) {
    case 'New':
    case 'Excellent':
      return 'positive';
    case 'Good':
      return 'neutral';
    case 'Unusable':
      return 'negative';
    default:
      return 'warning';
  }
}
